import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useEnvironment } from '../context/EnvironmentContext';
import { useLayoutVariant } from '../context/LayoutVariantContext';
import { toast } from '../lib/safeToast';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  RefreshCw,
  DollarSign,
  Activity,
  FileText,
  CheckCircle,
  Eye,
  LineChart,
  Shield,
  ArrowRight,
  AlertTriangle,
  MessageSquare,
  Send,
  Loader2,
  Upload,
  File,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { cn } from '../lib/utils';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Dashboard = () => {
  const { api, user, refreshUser, backendMisconfigured } = useAuth();
  const { isLive } = useEnvironment();
  const { isReference } = useLayoutVariant();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [showStatusView, setShowStatusView] = useState(false);
  const [infoRequests, setInfoRequests] = useState([]);
  const [infoLoading, setInfoLoading] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [respondLoading, setRespondLoading] = useState(false);
  const [responseFiles, setResponseFiles] = useState({});
  const [responseOptionalFiles, setResponseOptionalFiles] = useState({});
  const [expandedInfoReqs, setExpandedInfoReqs] = useState({}); // responded requests start collapsed
  const needsKyc = isLive && user?.onboarding_complete && user?.kyc_status !== 'approved';
  const isRejected = isLive && user?.onboarding_complete && user?.kyc_status === 'rejected';
  const isInfoRequested = user?.kyc_status === 'info_requested';
  const kycJustApproved = isLive && user?.kyc_status === 'approved' && user?.onboarding_complete;
  const [kycBannerDismissed, setKycBannerDismissed] = useState(false);
  useEffect(() => {
    if (user?.id && typeof sessionStorage !== 'undefined' && sessionStorage.getItem(`payzen_kyc_approved_seen_${user.id}`) === '1') {
      setKycBannerDismissed(true);
    }
  }, [user?.id]);
  const showKycApprovedBanner = kycJustApproved && !kycBannerDismissed;
  const handleDismissKycBanner = () => {
    setKycBannerDismissed(true);
    if (user?.id) sessionStorage.setItem(`payzen_kyc_approved_seen_${user.id}`, '1');
  };
  const [transactions, setTransactions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartPeriod, setChartPeriod] = useState('7d');
  const [chartLoading, setChartLoading] = useState(false);
  const [fxRate, setFxRate] = useState(null);
  const [fxChange, setFxChange] = useState(null);
  const [fxToCurrency, setFxToCurrency] = useState('IDR');
  const [fxPairIndex, setFxPairIndex] = useState(0);
  const fxPrevRef = React.useRef(null);

  // PayZen reference: same rotating FX pairs as atvi-payzen-reference dashboard (every 5s)
  const FX_PAIRS = [
    { from: 'AUD', to: 'IDR', rate: 10080, change: 2.5 },
    { from: 'AUD', to: 'PHP', rate: 38.5, change: 1.2 },
    { from: 'AUD', to: 'INR', rate: 56.2, change: -0.8 },
    { from: 'AUD', to: 'SGD', rate: 0.87, change: 0.5 },
    { from: 'AUD', to: 'VND', rate: 16420, change: 1.8 },
  ];

  const CHART_PERIODS = [
    { value: '7d', label: '7 days' },
    { value: '30d', label: '30 days' },
    { value: '90d', label: '3 months' },
    { value: '180d', label: '6 months' },
  ];

  useEffect(() => {
    fetchDashboardData();
  }, [isLive]);

  useEffect(() => {
    const pair = FX_PAIRS[fxPairIndex];
    const fetchFx = async () => {
      try {
        const res = await api.get(`/fx/rate?from=${pair.from}&to=${pair.to}`);
        const rate = res.data?.rate;
        const changePercent = res.data?.change_percent;
        if (rate != null) {
          setFxRate(rate);
          setFxToCurrency(pair.to);
          setFxChange(changePercent != null ? changePercent : pair.change);
        } else {
          setFxRate(pair.rate);
          setFxToCurrency(pair.to);
          setFxChange(pair.change);
        }
      } catch (_) {
        setFxRate(pair.rate);
        setFxToCurrency(pair.to);
        setFxChange(pair.change);
      }
    };
    fetchFx();
  }, [api, fxPairIndex]);

  useEffect(() => {
    const interval = setInterval(() => {
      setFxPairIndex((i) => (i + 1) % FX_PAIRS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    const kycApproved = !isLive || user?.kyc_status === 'approved';
    try {
      if (kycApproved) {
        const [balanceRes, txRes, analyticsRes] = await Promise.all([
          api.get('/banking/balance'),
          api.get('/banking/transactions?per_page=5'),
          api.get(`/analytics/summary?period=${chartPeriod}`),
        ]);
        setBalance(balanceRes.data);
        setTransactions(txRes.data.data || []);
        setAnalytics(analyticsRes.data);
      } else {
        setBalance(null);
        setTransactions([]);
        setAnalytics(null);
      }
    } catch (error) {
      if (kycApproved) toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsForChart = async (period) => {
    const kycApproved = !isLive || user?.kyc_status === 'approved';
    if (!kycApproved) return;
    setChartLoading(true);
    try {
      const res = await api.get(`/analytics/summary?period=${period}`);
      setAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to load chart data');
    } finally {
      setChartLoading(false);
    }
  };

  const handleChartPeriodChange = (period) => {
    setChartPeriod(period);
    fetchAnalyticsForChart(period);
  };

  const formatCurrency = (amount, currency = 'AUD') => {
    const num = parseFloat(amount);
    if (currency === 'USDC' || currency === 'USDT') {
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
    }
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
      }).format(num);
    } catch {
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
    }
  };

  const displayValue = (value, type = 'currency') => {
    if (!isLive) return value; // Sandbox: always show dummy data
    if (user?.kyc_status === 'approved') return value;
    if (type === 'currency') return '••••••';
    if (type === 'percent') return '•••%';
    if (type === 'count') return '••';
    return value;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCurrencyClass = (currency) => {
    const classes = {
      AUD: 'currency-aud',
      USD: 'currency-usd',
      EUR: 'currency-eur',
      USDC: 'currency-usdc',
      USDT: 'currency-usdt',
    };
    return classes[currency] || 'currency-aud';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner w-8 h-8" />
        </div>
      </Layout>
    );
  }

  const validateFileViewable = async (file, dataUrl) => {
    if (file.type.startsWith('image/')) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img.width > 0 && img.height > 0);
        img.onerror = () => resolve(false);
        img.src = dataUrl;
      });
    }
    if (file.type === 'application/pdf') {
      try {
        const b64 = dataUrl.split(',')[1];
        if (!b64 || b64.length < 20) return false;
        const first8 = atob(b64.slice(0, 12));
        return first8.startsWith('%PDF');
      } catch { return false; }
    }
    return true;
  };

  const handleInfoRespond = async (requestId) => {
    if (!responseText.trim()) { toast.error('Please provide your response'); return; }
    setRespondLoading(true);
    try {
      const documents = [];
      const req = infoRequests.find(r => r.id === requestId);
      const missingItems = req?.missing_items || [];
      const missingItemKeys = req?.missing_item_keys || [];
      for (const [key, file] of Object.entries(responseFiles)) {
        if (!key.startsWith(requestId)) continue;
        const idx = parseInt(key.split('_').pop(), 10);
        const docType = missingItemKeys[idx] || (missingItems[idx] || 'other').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const viewable = await validateFileViewable(file, dataUrl);
        if (!viewable) {
          toast.error(`"${file.name}" appears to be corrupted or invalid. Please upload a valid file.`);
          setRespondLoading(false);
          return;
        }
        documents.push({
          name: file.name,
          filename: file.name,
          type: docType,
          category: docType,
          mime: file.type,
          size: file.size,
          data_url: dataUrl,
        });
      }

      const optionalList = responseOptionalFiles[requestId] || [];
      for (let i = 0; i < optionalList.length; i++) {
        const file = optionalList[i];
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const viewable = await validateFileViewable(file, dataUrl);
        if (!viewable) {
          toast.error(`"${file.name}" appears to be corrupted or invalid. Please upload a valid file.`);
          setRespondLoading(false);
          return;
        }
        documents.push({
          name: file.name,
          filename: file.name,
          type: `info_request_attachment_${i}`,
          category: `info_request_attachment_${i}`,
          mime: file.type,
          size: file.size,
          data_url: dataUrl,
        });
      }

      await api.post(`/my/info-requests/${requestId}/respond`, {
        message: responseText.trim(),
        documents,
      });
      toast.success('Response submitted. Your application is back under review.');
      setRespondingTo(null);
      setResponseText('');
      setResponseFiles({});
      setResponseOptionalFiles(prev => { const next = { ...prev }; delete next[requestId]; return next; });
      setInfoRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'responded', merchant_response: responseText.trim() } : r));
      await refreshUser();
    } catch { toast.error('Failed to submit response'); }
    finally { setRespondLoading(false); }
  };

  if (showStatusView) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-12">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#0F172A] mb-2">Application status</h1>
            <p className="text-xl text-slate-600">Your next stop is business without borders.</p>
          </div>

          {/* Progress tracker */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-left mb-8">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4">
              {isInfoRequested ? 'Action required' : 'We are processing your application'}
            </h2>
            <p className="text-slate-600 mb-6">
              {isInfoRequested
                ? 'Our compliance team needs additional information from you. Please review the requests below and provide the required details.'
                : 'Our team is reviewing your information as quickly as possible. This process usually takes 1-3 business days.'}
            </p>
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-[#1C472B] flex items-center justify-center text-white"><CheckCircle className="w-6 h-6" /></div>
                <span className="text-sm font-medium mt-2">Sign up</span>
              </div>
              <div className="flex-1 h-0.5 bg-[#1C472B] mt-[-20px]" />
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${isInfoRequested ? 'bg-amber-500' : 'bg-[#1C472B]'}`}>
                  {isInfoRequested ? <AlertTriangle className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </div>
                <span className="text-sm font-medium mt-2">Verification</span>
                <span className={`text-xs ${isInfoRequested ? 'text-amber-600 font-semibold' : 'text-slate-500'}`}>
                  {isInfoRequested ? 'Info needed' : 'In review'}
                </span>
              </div>
              <div className="flex-1 h-0.5 bg-slate-200 mt-[-20px]" />
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-slate-200" />
                <span className="text-sm font-medium mt-2">Approved</span>
              </div>
            </div>
          </div>

          {/* Info Requests Section */}
          {isInfoRequested && (
            <div className="flex flex-col gap-8 mb-8">
              <h3 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-amber-600" />
                Information Requests
              </h3>
              {infoLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#0B6445]" /></div>
              ) : infoRequests.length === 0 ? (
                <Card className="border-amber-200">
                  <CardContent className="py-6 text-center text-slate-500">
                    <p>No pending requests found. Your application may have been updated.</p>
                  </CardContent>
                </Card>
              ) : (
                infoRequests.map((req) => {
                  const isResponded = req.status === 'responded';
                  const isExpanded = isResponded ? (expandedInfoReqs[req.id] === true) : true;
                  return (
                  <Card key={req.id} className={`border ${isResponded ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
                    <CardContent className="py-5 px-6">
                      {/* Header row: always visible; for responded, clickable to expand/collapse */}
                      <div
                        className={cn(
                          'flex items-start justify-between gap-3',
                          isResponded && 'cursor-pointer',
                          isResponded && !isExpanded && 'mb-0'
                        )}
                        onClick={() => isResponded && setExpandedInfoReqs((p) => ({ ...p, [req.id]: !p[req.id] }))}
                        role={isResponded ? 'button' : undefined}
                        aria-expanded={isResponded ? isExpanded : undefined}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                            isResponded ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {isResponded ? <><CheckCircle className="w-3 h-3" />Responded</> : <><AlertTriangle className="w-3 h-3" />Action Required</>}
                          </span>
                          {!isExpanded && isResponded && (
                            <span className="text-sm text-slate-600 truncate">{req.message}</span>
                          )}
                        </div>
                        <span className="text-xs text-slate-400 shrink-0">
                          {req.created_at ? new Date(req.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        </span>
                        {isResponded && (
                          <span className="shrink-0 text-slate-400">
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </span>
                        )}
                      </div>

                      {isExpanded && (
                        <>
                      <p className="text-sm text-slate-700 leading-relaxed mb-3 mt-3">{req.message}</p>

                      {req.requested_by_name && (
                        <p className="text-xs text-slate-400 mb-3">Requested by: {req.requested_by_name}</p>
                      )}

                      {req.merchant_response && (
                        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 mb-3">
                          <p className="text-xs font-medium text-emerald-700 mb-1">Your response:</p>
                          <p className="text-sm text-emerald-800">{req.merchant_response}</p>
                        </div>
                      )}

                      {/* Response form with missing items + upload + comments */}
                      {req.status === 'open' && (
                        <div className="mt-1 space-y-4">
                          {/* Missing items with upload buttons */}
                          {req.missing_items && req.missing_items.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-slate-600 mb-2.5 uppercase tracking-wide">Required items:</p>
                              <div className="space-y-2.5">
                                {req.missing_items.map((item, i) => {
                                  const fileKey = `${req.id}_${i}`;
                                  const uploadedFile = responseFiles[fileKey];
                                  return (
                                    <div key={i} className={`flex items-center gap-3 py-2.5 px-3.5 rounded-lg border ${uploadedFile ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}>
                                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${uploadedFile ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                        {uploadedFile ? <CheckCircle className="w-3.5 h-3.5" /> : (i + 1)}
                                      </div>
                                      <span className="text-sm text-slate-700 flex-1">{item}</span>
                                      {uploadedFile ? (
                                        <div className="flex items-center gap-2">
                                          <span className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full border border-emerald-200">
                                            <File className="w-3 h-3" />{uploadedFile.name}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={() => setResponseFiles(prev => { const n = { ...prev }; delete n[fileKey]; return n; })}
                                            className="text-slate-400 hover:text-red-500 p-0.5"
                                            title="Remove"
                                          >×</button>
                                        </div>
                                      ) : (
                                        <label className="cursor-pointer">
                                          <input
                                            type="file"
                                            className="hidden"
                                            accept=".png,.jpg,.jpeg,.pdf"
                                            onChange={async (e) => {
                                              const f = e.target.files?.[0];
                                              if (f) {
                                                const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
                                                if (!allowed.includes(f.type)) { toast.error('Only PNG, JPG, and PDF files are allowed'); return; }
                                                if (f.size > 2 * 1024 * 1024) { toast.error('File must be under 2 MB'); return; }
                                                const url = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
                                                const ok = await validateFileViewable(f, url);
                                                if (!ok) { toast.error(`"${f.name}" appears corrupted. Please upload a valid file.`); return; }
                                                setResponseFiles(prev => ({ ...prev, [fileKey]: f }));
                                                toast.success(`${f.name} attached`);
                                              }
                                            }}
                                          />
                                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#0B6445] hover:bg-[#095b3d] px-3 py-1.5 rounded-lg transition-colors cursor-pointer">
                                            <Upload className="w-3 h-3" /> Upload
                                          </span>
                                        </label>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Comments input */}
                          <div>
                            <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Your comments <span className="text-red-500">*</span></p>
                            <Textarea
                              placeholder="Describe the changes you've made or provide the requested information…"
                              value={respondingTo === req.id ? responseText : ''}
                              onChange={(e) => { if (respondingTo !== req.id) setRespondingTo(req.id); setResponseText(e.target.value); }}
                              onFocus={() => { if (respondingTo !== req.id) setRespondingTo(req.id); }}
                              className="min-h-[90px] text-sm"
                            />
                          </div>

                          {/* Optional document uploads */}
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Add documents (optional)</p>
                            <p className="text-xs text-slate-500">Attach photos, scans, or original forms if the compliance team asked for them.</p>
                            {(responseOptionalFiles[req.id] || []).length > 0 && (
                              <ul className="space-y-1.5">
                                {(responseOptionalFiles[req.id] || []).map((file, idx) => (
                                  <li key={idx} className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                                    <File className="w-4 h-4 text-slate-500 shrink-0" />
                                    <span className="truncate flex-1">{file.name}</span>
                                    <span className="text-xs text-slate-400">{(file.size / 1024).toFixed(1)} KB</span>
                                    <button
                                      type="button"
                                      onClick={() => setResponseOptionalFiles(prev => ({
                                        ...prev,
                                        [req.id]: (prev[req.id] || []).filter((_, i) => i !== idx),
                                      }))}
                                      className="text-slate-400 hover:text-red-500 p-0.5 shrink-0"
                                      title="Remove"
                                    >
                                      ×
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <div className="flex items-center gap-2">
                              <input
                                type="file"
                                id={`optional-doc-${req.id}`}
                                className="hidden"
                                accept=".png,.jpg,.jpeg,.pdf"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
                                  if (!allowed.includes(file.type)) { toast.error('Only PNG, JPG, and PDF allowed'); return; }
                                  if (file.size > 2 * 1024 * 1024) { toast.error('File must be under 2 MB'); return; }
                                  const url = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
                                  const ok = await validateFileViewable(file, url);
                                  if (!ok) { toast.error(`"${file.name}" appears corrupted. Please upload a valid file.`); return; }
                                  setResponseOptionalFiles(prev => ({ ...prev, [req.id]: [...(prev[req.id] || []), file] }));
                                  toast.success(`${file.name} added`);
                                  e.target.value = '';
                                }}
                              />
                              <label
                                htmlFor={`optional-doc-${req.id}`}
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0B6445] hover:text-[#095b3d] cursor-pointer"
                              >
                                <Upload className="w-4 h-4" /> Add document
                              </label>
                            </div>
                          </div>

                          {/* Submit */}
                          <div className="flex gap-2 pt-1">
                            <Button
                              size="sm"
                              disabled={respondLoading || !responseText.trim()}
                              onClick={() => handleInfoRespond(req.id)}
                              className="bg-[#0B6445] hover:bg-[#095b3d] text-white px-5"
                            >
                              {respondLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" />Submit Response</>}
                            </Button>
                            {!responseText.trim() && (
                              <p className="text-xs text-slate-400 self-center">Enter your comments to submit</p>
                            )}
                          </div>
                        </div>
                      )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                  );
                })
              )}
            </div>
          )}

          <div className="text-center">
            <Button className="h-11 px-8 rounded-xl bg-[#1C472B] hover:bg-[#143620] text-white font-semibold" onClick={() => setShowStatusView(false)}>
              Take me back to Dashboard
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const welcomeTitleClass = isReference
    ? 'text-3xl font-bold text-[#073B2E] tracking-tight'
    : 'text-[2.25rem] font-bold text-[#073B2E] leading-tight tracking-tight';
  const welcomeSubtitleClass = isReference
    ? 'text-[#0E1B16] opacity-70 mt-1'
    : 'text-base text-[#64748B] font-normal leading-relaxed';
  const cardClass = isReference ? 'border-[#DCE4E0] shadow-sm rounded-xl bg-white' : 'card';
  const sectionTitleClass = isReference ? 'text-xl font-bold text-[#073B2E] mb-4' : 'text-xl font-bold text-[#073B2E]';
  const cardContentClass = isReference ? 'p-6' : 'p-8';
  const cardHeaderPaddingClass = isReference ? 'px-6 pt-6 pb-2' : 'px-8 pt-8 pb-2';
  const cardContentPaddingClass = isReference ? 'px-6 pt-4 pb-6' : 'px-8 pt-6 pb-8';
  const todayStr = new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <Layout>
      <div data-testid="dashboard-page" className="space-y-8">
        {/* Header */}
        <div className={cn('flex flex-col sm:flex-row sm:items-center justify-between', isReference ? 'gap-4 mb-8' : 'gap-4')}>
          <div className={isReference ? 'space-y-1' : 'space-y-1.5'}>
            <h1 className={welcomeTitleClass}>
              Welcome back, {user?.name?.split(' ')[0]} 👋
            </h1>
            <p className={welcomeSubtitleClass}>
              {isRejected ? "We're sorry we couldn't approve your application this time." : isInfoRequested ? 'Action required — additional information needed' : needsKyc ? 'Your application is under review' : showKycApprovedBanner ? 'Your KYC has been approved — welcome aboard!' : isLive && !user?.onboarding_complete ? 'Complete your profile to get started' : "Here's your account overview and recent activity."}
            </p>
          </div>
          <div className={cn('flex items-center', isReference ? 'gap-3' : 'gap-4')}>
            {isReference && (
              <span className="text-sm text-[#0E1B16] opacity-60 whitespace-nowrap">{todayStr}</span>
            )}
            <Button
              onClick={fetchDashboardData}
              variant="outline"
              className={isReference ? 'h-11 px-5 rounded-lg border-[#DCE4E0] text-[#0B6445] hover:bg-[#E6F7F3] hover:text-[#073B2E] font-medium' : 'h-11 px-5 rounded-full border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E] font-medium'}
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {user?.role === 'admin' && (
          <div className="rounded-xl border border-[#0B6445]/30 bg-[#E6F7F3]/80 p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0B6445] flex items-center justify-center text-white">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-[#073B2E]">Administrator account</p>
                <p className="text-sm text-[#0E1B16]/70">Manage merchants, view platform audit logs, and access back-office tools.</p>
              </div>
            </div>
            <Link
              to="/backoffice/dashboard"
              className="inline-flex items-center gap-2 rounded-lg bg-[#0B6445] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#095b3d] transition-colors"
            >
              Open Back Office
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {isLive && !user?.onboarding_complete && (
          <div className="rounded-2xl border-2 border-amber-400 bg-amber-50/50 p-8 flex items-start gap-6">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <FileText className="w-6 h-6 text-amber-700" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-[#0F172A] mb-1">Continue onboarding</h2>
              <p className="text-slate-600 mb-4">
                You're off to a great start! Continue where you left off to help us verify your business.
              </p>
              <Button
                onClick={() => navigate('/onboarding')}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6"
                data-testid="continue-onboarding-btn"
              >
                Continue
              </Button>
            </div>
          </div>
        )}
        {isRejected && (
          <div className="rounded-2xl border-2 border-slate-200 bg-slate-50/50 p-8 flex items-start gap-6">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
              <FileText className="w-6 h-6 text-slate-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-[#0F172A] mb-1">Application not approved this time</h2>
              <p className="text-slate-600 mb-4">
                We're sorry we couldn't approve your application on this occasion. Don't worry — you can reapply when you're ready. We're here to help if you have questions or want to try again with updated information. Better luck next time!
              </p>
              <p className="text-sm text-slate-500">
                If you believe this was due to an error or would like to discuss your application, please contact our support team.
              </p>
            </div>
          </div>
        )}

        {needsKyc && !isRejected && (
          <div className={`rounded-2xl border-2 p-8 flex items-start gap-6 ${isInfoRequested ? 'border-amber-300 bg-amber-50/50' : 'border-blue-200 bg-blue-50/50'}`}>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isInfoRequested ? 'bg-amber-100' : 'bg-blue-100'}`}>
              {isInfoRequested ? <AlertTriangle className="w-6 h-6 text-amber-700" /> : <FileText className="w-6 h-6 text-blue-700" />}
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-[#0F172A] mb-1">
                {isInfoRequested ? 'Additional information required' : 'KYC/B verification in progress'}
              </h2>
              <p className="text-slate-600 mb-4">
                {isInfoRequested
                  ? 'Our compliance team has requested additional information to complete your verification. Please review the request and provide the required details.'
                  : 'Our team is reviewing your information. We will update you once the review is complete. Until then, you can explore the platform but transactions are not yet available.'}
              </p>
              <Button
                onClick={() => {
                  setShowStatusView(true);
                  if (isInfoRequested) {
                    setInfoLoading(true);
                    api.get('/my/info-requests')
                      .then(res => setInfoRequests(res.data?.data || []))
                      .catch(() => setInfoRequests([]))
                      .finally(() => setInfoLoading(false));
                  }
                }}
                className={`rounded-xl px-5 ${isInfoRequested ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-[#1C472B] hover:bg-[#143620] text-white'}`}
                data-testid="review-status-btn"
              >
                {isInfoRequested ? (
                  <><MessageSquare className="w-4 h-4 mr-2" />View required information</>
                ) : 'Review status'}
              </Button>
            </div>
          </div>
        )}

        {/* KYC Approved celebration banner */}
        {showKycApprovedBanner && (
          <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/60 p-8 flex items-start gap-6">
            <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-[#073B2E] mb-1">KYC Approved</h2>
              <p className="text-slate-600 mb-1">
                Congratulations! Your KYC/KYB verification has been approved. You now have full access to all platform services including live transactions, payments, and transfers.
              </p>
              <p className="text-sm text-slate-500 mb-5">
                As a next step, you will be asked to set up two-factor authentication (2FA) to secure your account for production use.
              </p>
              <Button
                onClick={handleDismissKycBanner}
                className="bg-[#0B6445] hover:bg-[#095b3d] text-white rounded-xl px-6"
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Got it, continue
              </Button>
            </div>
          </div>
        )}

        {/* Production backend not configured */}
        {backendMisconfigured && (
          <div className="rounded-xl border-2 border-amber-300 bg-amber-50 px-5 py-4 text-sm text-amber-800 flex items-start gap-3" role="alert">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Backend URL not configured</p>
              <p className="mt-1">Set <code className="bg-amber-100 px-1 rounded">REACT_APP_BACKEND_URL</code> to your Railway backend URL (e.g. <code className="bg-amber-100 px-1 rounded">https://your-app.up.railway.app</code>) in Vercel Environment Variables, then redeploy.</p>
            </div>
          </div>
        )}

        {/* Balance API unavailable notice */}
        {!backendMisconfigured && balance?.meta?.api_available === false && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 px-5 py-3.5 text-sm text-slate-600 flex items-center gap-2" role="status" style={{ marginTop: '1.5rem' }}>
            <AlertTriangle className="w-4 h-4 text-slate-400 shrink-0" />
            {balance?.meta?.message || 'A Balance service unavailable.'}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Available Balance */}
          <Card className={cardClass} data-testid="balance-card">
            <CardContent className={cardContentClass}>
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 bg-[#E6F7F3] rounded-2xl flex items-center justify-center">
                  <Wallet className="w-7 h-7 text-[#1C472B]" />
                </div>
                <span className={balance?.balance != null ? 'badge-success' : 'badge-pending'}>{balance?.balance != null ? 'Active' : 'Unavailable'}</span>
              </div>
              <p className="text-sm font-medium text-slate-600 mb-2">Available Balance</p>
              <p className="text-2xl font-bold text-[#0F172A] tracking-tight" data-testid="available-balance">
                {balance?.balance != null ? displayValue(formatCurrency(balance?.balance?.available || 0), 'currency') : '—'}
              </p>
            </CardContent>
          </Card>

          {/* Total Credits */}
          <Card className={cardClass} data-testid="credits-card">
            <CardContent className={cardContentClass}>
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 bg-[#E6F7F3] rounded-2xl flex items-center justify-center">
                  <TrendingUp className="w-7 h-7 text-[#1C472B]" />
                </div>
                <span className="stat-change-positive">
                  <ArrowUpRight className="w-4 h-4" />
                  {displayValue('+12.5%', 'percent')}
                </span>
              </div>
              <p className="text-sm font-medium text-slate-600 mb-2">Total Credits</p>
              <p className="text-2xl font-bold text-[#0F172A] tracking-tight" data-testid="total-credits">
                {balance?.balance != null ? displayValue(formatCurrency(balance?.balance?.total_credits || 0), 'currency') : '—'}
              </p>
            </CardContent>
          </Card>

          {/* Total Debits */}
          <Card className={cardClass} data-testid="debits-card">
            <CardContent className={cardContentClass}>
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center">
                  <TrendingDown className="w-7 h-7 text-[#F08080]" />
                </div>
                <span className="stat-change-negative">
                  <ArrowDownRight className="w-4 h-4" />
                  {displayValue('-5.2%', 'percent')}
                </span>
              </div>
              <p className="text-sm font-medium text-slate-600 mb-2">Total Debits</p>
              <p className="text-2xl font-bold text-[#0F172A] tracking-tight" data-testid="total-debits">
                {balance?.balance != null ? displayValue(formatCurrency(balance?.balance?.total_debits || 0), 'currency') : '—'}
              </p>
            </CardContent>
          </Card>

          {/* Reserved */}
          <Card className={cardClass} data-testid="reserved-card">
            <CardContent className={cardContentClass}>
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center">
                  <Clock className="w-7 h-7 text-amber-600" />
                </div>
                <span className="badge-pending">Pending</span>
              </div>
              <p className="text-sm font-medium text-slate-600 mb-2">Reserved Outgoing</p>
              <p className="text-2xl font-bold text-[#0F172A] tracking-tight" data-testid="reserved-amount">
                {balance?.balance != null ? displayValue(formatCurrency(balance?.balance?.reserved_outgoing || 0), 'currency') : '—'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Accounts shortcut — full breakdown lives on Accounts page */}
        {balance?.ledger_balances && Object.keys(balance.ledger_balances).length > 0 && (
          <Link
            to="/accounts"
            className={`block rounded-xl border-2 border-[#DCE4E0] ${cardClass} p-4 hover:border-[#0B6445]/40 hover:bg-[#F7FAF9] transition-colors mt-8`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#E6F7F3] flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-[#0B6445]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0F172A]">Multi-currency accounts</p>
                  <p className="text-sm text-slate-500">{Object.keys(balance.ledger_balances).length} ledger{Object.keys(balance.ledger_balances).length !== 1 ? 's' : ''} · View balances by currency</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 shrink-0" />
            </div>
          </Link>
        )}

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Volume Chart */}
          <Card className={`${cardClass} lg:col-span-2`} data-testid="volume-chart">
            <CardHeader className={cardHeaderPaddingClass}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className={sectionTitleClass}>
                    Transaction Volume
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    {CHART_PERIODS.find((p) => p.value === chartPeriod)?.label ?? '30 days'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {CHART_PERIODS.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => handleChartPeriodChange(p.value)}
                      disabled={chartLoading}
                      className={cn(
                        'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                        chartPeriod === p.value
                          ? 'bg-[#0B6445] text-white'
                          : 'bg-[#F7FAF9] text-[#0E1B16] border border-[#DCE4E0] hover:bg-[#E6F7F3] hover:border-[#0B6445]'
                      )}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className={cardContentPaddingClass}>
              <div className="relative w-full min-h-[200px]" style={{ height: 280 }}>
                {chartLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 rounded-xl">
                    <div className="spinner w-8 h-8" />
                  </div>
                )}
                {isLive && user?.kyc_status !== 'approved' && !chartLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/90 rounded-xl">
                    <p className="text-slate-500 font-medium text-lg">••••••</p>
                  </div>
                )}
                <ResponsiveContainer width="100%" height={280} minWidth={0}>
                  <AreaChart data={analytics?.daily_volume || []}>
                    <defs>
                      <linearGradient id="colorInflow" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1C472B" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#1C472B" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorOutflow" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F08080" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#F08080" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: '#64748B' }}
                      tickFormatter={(value) => value.split('-').slice(1).join('/')}
                    />
                    <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="inflow"
                      stroke="#1C472B"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorInflow)"
                      name="Inflow"
                    />
                    <Area
                      type="monotone"
                      dataKey="outflow"
                      stroke="#F08080"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorOutflow)"
                      name="Outflow"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className={cardClass} data-testid="quick-stats">
            <CardHeader className={cardHeaderPaddingClass}>
              <CardTitle className={sectionTitleClass}>
                Quick Stats
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                {CHART_PERIODS.find((p) => p.value === chartPeriod)?.label ?? '30 days'} summary
              </p>
            </CardHeader>
            <CardContent className={`${cardContentPaddingClass} space-y-8`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#E6F7F3] rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-[#1C472B]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-0.5">Net Flow</p>
                    <p className="text-lg font-bold text-[#1C472B]">
                      {displayValue(formatCurrency(analytics?.net_flow || 0), 'currency')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#E6F7F3] rounded-xl flex items-center justify-center">
                    <Activity className="w-6 h-6 text-[#1C472B]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-0.5">Transactions</p>
                    <p className="text-lg font-bold text-[#0F172A]">
                      {displayValue(String(analytics?.transaction_count || 0), 'count')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#E6F7F3] rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-[#1C472B]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-0.5">Avg Transaction</p>
                    <p className="text-lg font-bold text-[#0F172A]">
                      {displayValue(formatCurrency(analytics?.average_transaction || 0), 'currency')}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Transactions */}
        <Card className={cardClass} data-testid="recent-transactions">
          <CardHeader className={cn('flex flex-row items-center justify-between', isReference ? 'px-6 pt-6 pb-4' : 'px-8 pt-8 pb-4')}>
            <div>
              <CardTitle className={sectionTitleClass}>
                Recent Transactions
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">Latest activity on your account</p>
            </div>
            <Button
              variant="ghost"
              className="text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E] font-medium"
              onClick={() => window.location.href = '/transactions'}
            >
              View All
            </Button>
          </CardHeader>
          <CardContent className={isReference ? 'px-6 pt-4 pb-6' : 'px-8 pt-4 pb-8'}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-header">Transaction</th>
                    <th className="table-header">Counterparty</th>
                    <th className="table-header">Date</th>
                    <th className="table-header">Amount</th>
                    <th className="table-header">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              tx.direction === 'credit'
                                ? 'bg-[#E6F7F3]'
                                : 'bg-red-50'
                            }`}
                          >
                            {tx.direction === 'credit' ? (
                              <ArrowDownRight className="w-5 h-5 text-[#1C472B]" />
                            ) : (
                              <ArrowUpRight className="w-5 h-5 text-[#F08080]" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-[#0F172A]">
                              {tx.type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                            </p>
                            <p className="text-xs text-slate-500 font-mono">{(isLive && user?.kyc_status !== 'approved') ? '••••••' : tx.reference}</p>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">{(isLive && user?.kyc_status !== 'approved') ? '••••••' : tx.counterparty}</td>
                      <td className="table-cell text-slate-500">{formatDate(tx.date)}</td>
                      <td className="table-cell">
                        <span
                          className={`font-semibold ${
                            tx.direction === 'credit' ? 'text-[#1C472B]' : 'text-[#F08080]'
                          }`}
                        >
                          {tx.direction === 'credit' ? '+' : '-'}
                          {displayValue(formatCurrency(tx.amount, tx.currency), 'currency')}
                        </span>
                      </td>
                      <td className="table-cell">
                        <span
                          className={
                            tx.status === 'completed'
                              ? 'badge-success'
                              : tx.status === 'pending'
                              ? 'badge-pending'
                              : 'badge-failed'
                          }
                        >
                          {tx.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Screen 2: Invite Friends + FX Rate Alert in one line (below recent transactions, reference style) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-purple-200 bg-white shadow-sm p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
              <Activity className="w-6 h-6 text-purple-700" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-[#0F172A] text-lg">Invite Friends</h3>
              <p className="text-sm text-slate-600 mt-0.5">Share your referral code</p>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-xl border border-purple-200 bg-white px-4 py-2.5 text-sm font-medium text-purple-700 hover:bg-purple-50 hover:text-purple-900 transition-colors"
            >
              Share
            </button>
          </div>
          <div className="rounded-2xl border border-sky-200 bg-white shadow-sm p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
              <LineChart className="w-6 h-6 text-sky-700" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-[#0F172A] text-lg">FX Rate Alert</h3>
              <p className="text-sm text-slate-600 mt-0.5">
                1 AUD = {fxRate != null ? (typeof fxRate === 'number' && fxRate >= 100 ? fxRate.toLocaleString() : Number(fxRate).toFixed(2)) : '—'} {fxToCurrency}
              </p>
            </div>
            {(fxChange != null || fxRate != null) && (
              <span className={cn(
                'shrink-0 rounded-lg text-white text-sm font-semibold px-3 py-1.5',
                (fxChange ?? 0) >= 0 ? 'bg-green-500' : 'bg-red-500'
              )}>
                {(fxChange ?? 0) >= 0 ? '+' : ''}{(fxChange ?? 0).toFixed(1)}%
              </span>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
